import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { HREF } from '@core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IncludesPipe } from '../../../pipes';
import { FilterOption } from '../../product-filter.model';
import { MatPseudoCheckbox } from '@angular/material/core';
import { AccessibleClickDirective } from '../../../directives/accessible-click.directive';

@Component({
  selector: 'lib-mobile-select',
  templateUrl: './mobile-select.component.html',
  styleUrls: ['./mobile-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    IncludesPipe,
    MatPseudoCheckbox,
    AccessibleClickDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MobileSelectComponent),
      multi: true,
    },
  ],
})
export class MobileSelectComponent<T> implements OnChanges, ControlValueAccessor {
  readonly title = input.required<string>();
  readonly options = input.required<FilterOption[]>();
  readonly hasHeader = input(true);
  readonly multiple = input(false);
  readonly opened = input(false);

  readonly isSelectOpened = signal(false);
  readonly selectedOption = signal<T | null>(null);
  readonly selectedOptions = signal<T[]>([]);

  readonly vm = computed(() => ({
    title: this.title(),
    options: this.options(),
    isSelectOpened: this.isSelectOpened(),
    selectedOption: this.selectedOption(),
    selectedOptions: this.selectedOptions(),
  }));

  readonly href = HREF;

  private onChange: (value: T | T[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if ('opened' in changes) {
      this.isSelectOpened.set(this.opened());
    }
  }

  writeValue(value: any): void {
    this.multiple() ? this.selectedOptions.set(value ?? []) : this.selectedOption.set(value ?? null);

    this.onChange(this.selectedOptions());
    this.onTouched();
  }

  registerOnChange(fn: (value: T | T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  toggleIsSelectOpened(): void {
    this.isSelectOpened.update((prev) => !prev);
  }

  toggleSelectOption(optionValue: T): void {
    this.selectedOption.set(optionValue);
    this.onChange(this.selectedOption()!);
    this.onTouched();
  }

  toggleSelectOptions({ checked }: MatCheckboxChange, optionValue: T): void {
    !checked
      ? this.selectedOptions.update((prev) => prev.filter((item) => item !== optionValue))
      : this.selectedOptions.update((prev) => [...prev, optionValue]);

    this.onChange(this.selectedOptions());
    this.onTouched();
  }
}
